import { IconName } from '../components/Icon/Icon.interface';
import { QuestionCode } from './api/pep';
import { Option } from './components/common';
import { DocumentType, SigningFormType } from './document';
import { Language } from './i18n';
import { Profession } from './user';

export type CurrencyPlacement = 'before' | 'after';

export type ExtraBasicInfoQuestions = Partial<
  Record<
    QuestionCode,
    {
      enabled: boolean;
      question: string;
      description?: string;
      tooltip?: string;
      priority: number;
      extraProps?: Record<string, unknown>;
    }
  >
>;

export type AppConfigurations = {
  links: {
    privacyPolicyText: string;
    privacyPolicyURL: string;
    termsAndConditionsText: string;
    termsAndConditionsURL: string;
    termsOfUseText: string;
    termsOfUseURL: string;
    appTermsAndConditionsText: string;
    appTermsAndConditionsURL: string;
    accountOpeningRequirementUrl?: string;
    biometricConsentText: string;
    biometricConsentURL: string;
    icbWeb: string;
    icbIOS: string;
    icbAndroid: string;
    savingsPlusURL?: string;
    picaURL?: string;
    personalChequingURL?: string;
    timeSaverURL?: string;
    eFreeURL?: string;
    eFreeUsdURL?: string;
    usdSavingsURL?: string;
    eurSavingsURL?: string;
  };
  logo: string;
  brandingLogoLight: string;
  brandingImageInfoPage: string;
  brandingImageAddressPage: string;
  headerLogoIconOnDark?: IconName;
  headerLogoIconOnLight?: IconName;
  headerLogoIconSx?: { width: number; height: number };
  defaultCountryCode: string;
  defaultIdentityType?: DocumentType;
  locale: string;
  isoCode: string;
  dateFormat: string;
  defaultCurrency: string;
  currencyPlacement: Partial<Record<Language, CurrencyPlacement>>;
  accountTypeMaxAmount: number;
  scanIDAutoCaptureSeconds: number;
  proofOfEmploymentDescriptionByType: Partial<
    Record<
      DocumentType,
      {
        title: string;
        description?: string;
      }
    >
  >;
  idDocumentTypes: DocumentType[];
  minorOnlyIdDocumentTypes?: DocumentType[];
  idCaptureRequirement?:
    | 'idCard'
    | 'idCardFront'
    | 'idCardBack'
    | 'passport'
    | 'idCardOrPassport'
    | 'idCardAndPassport';
  idTypesWithoutExpiryDate?: string[];
  idDefaultExpiryDate: string;
  requiredLocalTIN: boolean;
  formsOnEachAccountType: SigningFormType[];
  autoCompleteAdultWithNoDocumentSigning: boolean;
  autoApproveUnemployedUsers?: boolean;
  autoApproveUnemployedProfessions?: Profession[];
  availableLanguages: Language[];
  residenceOptions?: Option[];
  jointAccountRelationshipOptions: Option[];
  childLivesWithRelationshipOptions?: Option[];
  addressCityOptions?: Option[];
  shouldShowMaritalStatus: boolean;
  shouldShowGDPRDrawer: boolean;
  extraPhoneOptions?: Option[];
  maxTaxResidencySelection: number;
  // TODO: EXC - remove when rolling out to all tenants
  isExcEnabled?: boolean;
  showRiskSectorFlagQuestion?: boolean;
  showSelfEmployedWithRiskSectorFlagQuestion?: boolean;
  showSelfEmployedStartupQuestion?: boolean;
  isBranchSelectionEnabled?: boolean;
  excludedBranchCodes: string[];
  shouldRequireMaritalStatus?: boolean;
  shouldAddSecondPhoneNumber?: boolean;
  shouldShowUsername?: boolean;
  shouldShowIssuance?: boolean;
  shouldShowCityIssuance?: boolean;
  shouldShowCityOfBirth?: boolean;
  shouldShowMotherMaidenName?: boolean;
  shouldShowEmailNotificationPopup?: boolean;
  shouldShowLeftBrandingLogo?: boolean;
  shouldShowRightBrandingLogo?: boolean;
  shouldRejectNonResidentMinor: boolean;
  canAbandonApplication?: boolean;
  isProofOfRelationshipBlockedByBasicInfo?: boolean;
  existingCustomer?: {
    maxAccountsPerType: number;
  };
  isMultipleMonthlyDeposits?: boolean;
  overridePhoneNumberSchema?: Record<
    string,
    { maxLength?: number; pattern?: string }
  >;
};
export type StoreAppConfigurations = Omit<
  AppConfigurations,
  'getExtraBasicInfoQuestions'
>;
export enum FeatureName {
  NoIdentityID = 'no_identity_id',
  SecondID = 'second_id',
  LivenessSystem = 'liveness_system',
  DisableLivenessRecord = 'disable_liveness_record',
  RightStartAccount = 'right_start_account',
  RightStartComingSoon = 'right_start_coming_soon',
  BusinessComingSoon = 'business_coming_soon',
  SharedWithSomeoneElseComingSoon = 'shared_with_someone_else_coming_soon',
  NewApplicationTypeSelection = 'new_application_type_selection',
  MailingAddress = 'mailing_address',
  Remediation = 'remediation',
  YourFinances = 'your_finances',
  AdobeSignForm = 'adobe_sign_form',
}

export enum LivenessSystem {
  VU = 'vu',
  AWS = 'aws',
  IDM = 'idm',
}

export type Feature = {
  id: string;
  feature: FeatureName;
  value: string;
  enabled?: boolean;
};
